import { z } from 'zod';

/**
 * 12-factor SPA (backbone §7): one compiled artifact, environment specifics fetched from
 * `/config.json` at bootstrap. No per-environment rebuild, and a SPA holds no secrets at all.
 */
export const runtimeConfigSchema = z.object({
  environment: z.enum(['local', 'dev', 'stage', 'prod']),
  apiBaseUrl: z.string(),
  auth: z.object({
    /**
     * `oidc` is the real one, against whichever issuer `issuer` names — Keycloak locally,
     * Cognito in EKS. There is no per-provider value, because the console does not know which
     * issuer it is talking to and has no use for knowing (decisions T-3/T-4).
     */
    provider: z.enum(['mock', 'oidc', 'legacy-session']),
    /** The `iss` every token carries, e.g. `http://localhost:8080/realms/eis`. */
    issuer: z.string().optional(),
    clientId: z.string().optional(),
    redirectUri: z.string().optional(),
    scopes: z.array(z.string()).default(['openid', 'profile', 'email']),
    /** Only honoured by the mock provider. */
    defaultPersona: z.string().default('operations-manager'),
    /**
     * `legacy-session` only (SDD-012, U-6'). The legacy admin's origin — the ONLY origin whose
     * `postMessage` reply is accepted as a session — and the origin of the legacy API that
     * refreshes it (`PUBLIC_API_URL` in the legacy build, e.g. `https://apiv2-test.emil.de`).
     * Both differ per environment and are supplied at deploy time like every other address here.
     */
    legacyOrigin: z.url().optional(),
    legacyApiBaseUrl: z.url().optional(),
  }),
  i18n: z.object({
    /** Supported languages live per environment, not in code (Addendum B.2). */
    supportedLanguages: z.array(z.string()).min(1).default(['de', 'en']),
    defaultLanguage: z.string().default('de'),
  }),
  /**
   * Which chrome the shell wears (SDD-012, U-2). `suite` is the legacy EMIL Insurance Suite look
   * for the transition; `berry` is the console's own. The value changes tokens and the chrome's
   * slots and nothing else — feature code never reads it. Default `suite` until the legacy admin
   * is retired; the redesign is this one value flipping.
   */
  chrome: z
    .object({
      variant: z.enum(['suite', 'berry']).default('suite'),
      /**
       * The legacy admin's origin for this environment (U-5'): where Back navigates when this
       * window has no opener, and where the suite's own assets are served from. Per environment,
       * like every other address in this file; the default is the local two-origin simulation.
       */
      suiteUrl: z.url().default('https://suite.localhost'),
      /**
       * Tenant branding (U-7'): the legacy has no live branding endpoint, so the accent
       * (`--main-color`, a hex colour and nothing else — it is written into a style sheet) and
       * the logo come from here. Absent, the demo tenant's values apply: `#1dc4e9` and the
       * legacy's own `/assets/logos/emil_group.png` under `suiteUrl`.
       */
      accent: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'chrome.accent must be a #rrggbb colour')
        .optional(),
      logoUrl: z.url().optional(),
      tenantName: z.string().min(1).optional(),
      /**
       * Tenants this ENVIRONMENT presents under another name, keyed by the token's tenant id
       * (SDD-022 D-5, D-8, T-53): the badge and the profile chip show `name`, and `notice: true`
       * opens the demo notice once per browser session after sign-in. A deployment value, never a
       * realm, a migration or domain data — the token's claims are unchanged. Absent: `{}`.
       */
      demoTenants: z
        .record(
          z.string().min(1),
          z.object({ name: z.string().min(1), notice: z.boolean().default(false) }),
        )
        .default({}),
    })
    .default({ variant: 'suite', suiteUrl: 'https://suite.localhost', demoTenants: {} }),
  /**
   * Serve `/api` from the in-browser mock (MSW) whatever the auth provider. Absent, the mock runs
   * with `provider: "mock"` only. The BDD session scenarios set it to run the REAL OIDC adapter
   * against the mocked session endpoints (SDD-019 §12); no deployed config ever does.
   */
  mockApi: z.boolean().optional(),
  /**
   * The module set this HOST serves (decision T-59, SDD-024 A-1): one build, several hosts. Absent,
   * every registered module is served — the console is unchanged. The Data Suite at `data.<env>`
   * serves the same image with `["reporting"]`; the menu and the home route follow the set.
   */
  modules: z.array(z.string().min(1)).min(1).optional(),
  /** OpenFeature config-file provider (backbone §9). Flags are rollout, never authorization. */
  flags: z.record(z.string(), z.boolean()).default({}),
  telemetry: z
    .object({ dsn: z.string().optional(), sampleRate: z.number().min(0).max(1).default(0.1) })
    .default({ sampleRate: 0.1 }),
});

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(url = '/config.json'): Promise<RuntimeConfig> {
  if (cached !== null) return cached;
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Runtime config unavailable (${String(response.status)} from ${url}).`);
  }
  cached = runtimeConfigSchema.parse(await response.json());
  return cached;
}

/** Tests and Storybook inject a config instead of fetching one. */
export function setRuntimeConfig(config: RuntimeConfig): void {
  cached = runtimeConfigSchema.parse(config);
}

export function getRuntimeConfig(): RuntimeConfig {
  if (cached === null) {
    throw new Error('Runtime config not loaded yet — call loadRuntimeConfig() during bootstrap.');
  }
  return cached;
}

export function resetRuntimeConfig(): void {
  cached = null;
}
